import React from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orderService } from '../services/order.service';
import { Button } from '../components/ui/button';
import { CircleCheck, ShieldCheck, CreditCard, Truck, Loader2, Package, Banknote } from 'lucide-react';
import { Separator } from '../components/ui/separator';
import { env } from '../config/env';

export const OrderSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const navState = location.state as { cash_on_delivery?: boolean } | null;
  const { data: orderData, isLoading: loading, error } = useQuery({
    queryKey: ['order-success', id],
    queryFn: () => orderService.getOrder(id || ''),
    enabled: !!id,
  });

  const order = orderData?.order;
  const items = orderData?.items;
  const payment = orderData?.payment;

  // Check if COD order
  const isCod = navState?.cash_on_delivery === true
    || order?.payment_type === 'CASH_ON_DELIVERY'
    || payment?.provider === 'cash_on_delivery';

  // Check if payment is pending (not COD and payment status is PENDING)
  const isPaymentPending = !isCod && payment?.status === 'PENDING';

  // Get payment provider from config
  const paymentProvider = env.PAYMENT_GATEWAY?.toLowerCase() || payment?.provider?.toLowerCase();

  // Handle payment based on provider
  const handlePayment = async () => {
    if (!order || !payment) return;

    const providerOrderId = payment.provider_order_id;
    const orderId = order.id;

    switch (paymentProvider) {
      case 'cashfree':
        // For Cashfree, navigate to payment URL or use provider_order_id
        if (payment.provider_response?.payment_url) {
          window.location.href = payment.provider_response.payment_url;
        } else if (providerOrderId) {
          // Construct Cashfree payment URL
          const cashfreeUrl = `https://pgi.cashfree.com/payment/pf?transId=${providerOrderId}`;
          window.location.href = cashfreeUrl;
        }
        break;

      case 'razorpay':
        // For Razorpay, initialize checkout
        if ((window as any).Razorpay) {
          const options = {
            key: env.RAZORPAY_KEY_ID,
            amount: payment.amount * 100, // Convert to paise
            currency: payment.currency,
            name: env.COMPANY_NAME,
            description: `Order #${orderId}`,
            order_id: providerOrderId,
            handler: (response: any) => {
              // Handle successful payment - call backend to verify
              console.log('Razorpay payment success:', response);
              // Optionally redirect to success or verify payment
              navigate(`/order-success/${orderId}`, { replace: true });
            },
            modal: {
              ondismiss: () => {
                console.log('Payment modal dismissed');
              }
            }
          };
          const razorpay = new (window as any).Razorpay(options);
          razorpay.open();
        } else {
          // Fallback: navigate to payment page
          navigate(`/orders/${orderId}/pay`);
        }
        break;

      default:
        // Default: navigate to payment page
        navigate(`/orders/${orderId}/pay`);
    }
  };

  // Load Razorpay script if needed
  React.useEffect(() => {
    if (paymentProvider === 'razorpay' && !(window as any).Razorpay) {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, [paymentProvider]);

  if (loading) {
    return (
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 flex justify-center">
        <div className="text-center space-y-6">
          <div className="size-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Loader2 className="size-6 animate-spin text-primary" />
          </div>
          <h2 className="text-3xl font-black tracking-tight text-[#111418] dark:text-white uppercase">
            Processing Order...
          </h2>
          <p className="text-gray-500 font-medium tracking-tight">
            Please wait while we finalize your purchase.
          </p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 text-center space-y-4">
        <h2 className="text-2xl font-black text-destructive tracking-tight uppercase">
          Order Not Found
        </h2>
        <p className="text-muted-foreground font-medium">
          This transaction does not exist in our secure archives.
        </p>
        <Button asChild>
          <Link to="/" className="rounded-xl">Return to Home</Link>
        </Button>
      </div>
    );
  }

  // Show success state for COD or paid orders
  const showOrderCompleted = isCod || payment?.status === 'SUCCESS' || payment?.status === 'paid';

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-6 md:py-10">
      <header className="mb-16 text-center">
        <div className={`size-20 mx-auto rounded-full flex items-center justify-center mb-6 ${showOrderCompleted ? 'bg-emerald-500/10' : 'bg-amber-500/10'}`}>
          {showOrderCompleted ? <CircleCheck className="size-10 text-emerald-500" /> : <CreditCard className="size-10 text-amber-500" />}
        </div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tight text-[#111418] dark:text-white uppercase leading-none">
          {showOrderCompleted ? 'Order Placed Successfully!' : 'Payment Pending'}
        </h1>
        <p className="text-xl font-medium tracking-tight text-gray-500 dark:text-gray-400 mt-4">
          {showOrderCompleted
            ? isCod
              ? 'Thank you! Your order has been confirmed. Pay when it arrives at your doorstep.'
              : 'Thank you! Your payment has been confirmed and your order is being processed.'
            : 'Your order has been created. Please complete the payment to confirm your order.'}
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16">
        {/* Main Content */}
        <div className="lg:col-span-8 space-y-12">
          {/* Order Summary */}
          <section className="space-y-8">
            <h2 className="text-xl font-black tracking-tight text-[#111418] dark:text-white uppercase">
              Order Summary
            </h2>
            
            <div className="bg-gray-50 dark:bg-gray-900 rounded-[2.5rem] p-8 md:p-10 space-y-6">
              {/* Order Details */}
              <div className="space-y-4">
                <div className="flex justify-between items-center text-sm font-bold text-gray-500">
                  <span>Order #</span>
                  <span className="font-black text-[#111418] dark:text-white">{order.id}</span>
                </div>
                <div className="flex justify-between items-center text-sm font-bold text-gray-500">
                  <span>Date</span>
                  <span className="font-black text-[#111418] dark:text-white">
                    {new Date(order.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm font-bold text-gray-500">
                  <span>Status</span>
                  <span className={`font-black ${order.status === 'DELIVERED' ? 'text-emerald-500 dark:text-emerald-400' : 
                    order.status === 'PROCESSING' ? 'text-amber-500 dark:text-amber-400' : 'text-primary dark:text-primary'}`}>
                    {order.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm font-bold text-gray-500">
                  <span>Payment Method</span>
                  <span className="font-black text-[#111418] dark:text-white">
                    {isCod ? 'Cash on Delivery' : (payment?.provider === 'razorpay' ? 'Razorpay' : payment?.provider === 'cashfree' ? 'Cashfree' : 'Online Payment')}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm font-bold text-gray-500">
                  <span>Payment Status</span>
                  <span className={`font-black ${
                    showOrderCompleted ? 'text-emerald-500' : 
                    payment?.status === 'FAILED' ? 'text-red-500' : 'text-amber-500'
                  }`}>
                    {isCod ? 'Pay on Delivery' : 
                     payment?.status === 'SUCCESS' || payment?.status === 'paid' ? 'Paid' :
                     payment?.status === 'FAILED' || payment?.status === 'failed' ? 'Failed' : 'Pending'}
                  </span>
                </div>
              </div>

              {/* Items List */}
              <div className="space-y-4">
                <h3 className="text-lg font-black tracking-tight text-[#111418] dark:text-white uppercase">
                  Items ({items?.length || 0})
                </h3>
                <div className="space-y-3">
                  {items?.map((item) => (
                    <div key={item.id} className="flex items-center gap-4 p-4 bg-white dark:bg-black/5 rounded-xl">
                      <div className="size-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Package className="size-4 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-black text-[#111418] dark:text-white">{item.name}</h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          QTY: {item.quantity} × ${item.unit_price.toFixed(2)}
                        </p>
                      </div>
                      <div className="text-right font-black text-[#111418] dark:text-white">
                        ${item.total_amount.toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pricing Summary */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6 space-y-4">
                <div className="flex justify-between items-center text-sm font-bold text-gray-500">
                  <span>Subtotal</span>
                  <span>${order.subtotal_amount.toFixed(2)}</span>
                </div>
                {order.shipping_amount > 0 && (
                  <div className="flex justify-between items-center text-sm font-bold text-gray-500">
                    <span>Shipping</span>
                    <span>${order.shipping_amount.toFixed(2)}</span>
                  </div>
                )}
                {order.tax_amount > 0 && (
                  <div className="flex justify-between items-center text-sm font-bold text-gray-500">
                    <span>Tax</span>
                    <span>${order.tax_amount.toFixed(2)}</span>
                  </div>
                )}
                {order.discount_amount > 0 && (
                  <div className="flex justify-between items-center text-sm font-bold text-green-500">
                    <span>Discount</span>
                    <span>-${order.discount_amount.toFixed(2)}</span>
                  </div>
                )}
                <Separator className="my-4" />
                <div className="flex justify-between items-center text-xl font-black text-[#111418] dark:text-white">
                  <span>Total</span>
                  <span>${order.total_amount.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </section>

          {/* Next Steps - Only show for completed orders */}
          {showOrderCompleted && (
            <section className="space-y-8">
              <h2 className="text-xl font-black tracking-tight text-[#111418] dark:text-white uppercase">
                What's Next?
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 dark:bg-gray-900 rounded-[2rem] p-6 text-center border border-border/40">
                  <div className="size-12 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <Truck className="size-6 text-primary" />
                  </div>
                  <h3 className="font-black text-[#111418] dark:text-white">Order Processing</h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    Our team is preparing your items for shipment. You'll receive a confirmation email shortly.
                  </p>
                </div>
                
                <div className="bg-gray-50 dark:bg-gray-900 rounded-[2rem] p-6 text-center border border-border/40">
                  <div className="size-12 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <ShieldCheck className="size-6 text-primary" />
                  </div>
                  <h3 className="font-black text-[#111418] dark:text-white">Order Tracking</h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    Track your order in real-time using the tracking link sent to your email.
                  </p>
                </div>
              </div>
            </section>
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-4">
          <div className="bg-[#111418] text-white rounded-[2.5rem] p-8 md:p-10 space-y-6 sticky top-20">
            {showOrderCompleted ? (
              // Order Completed State
              <>
                <h2 className="text-2xl font-black tracking-tight uppercase">
                  {isCod ? 'Order Confirmed' : 'Payment Complete'}
                </h2>

                <div className="space-y-4">
                  <div className="flex justify-between items-center text-sm font-bold">
                    <span className="text-gray-400">Order Total</span>
                    <span className="text-2xl font-black text-primary">${order.total_amount.toFixed(2)}</span>
                  </div>
                  {isCod && (
                    <div className="flex items-center gap-3 p-4 bg-emerald-500/10 rounded-2xl">
                      <Banknote className="size-5 text-emerald-400" />
                      <span className="text-sm font-bold text-emerald-400">Cash on Delivery — Pay when delivered</span>
                    </div>
                  )}
                </div>
              </>
            ) : (
              // Payment Pending State
              <>
                <h2 className="text-2xl font-black tracking-tight uppercase">Payment Pending</h2>

                <div className="space-y-4">
                  <div className="flex justify-between items-center text-sm font-bold">
                    <span className="text-gray-400">Amount Due</span>
                    <span className="text-2xl font-black text-amber-400">${payment?.amount || order.total_amount}</span>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-amber-500/10 rounded-2xl">
                    <CreditCard className="size-5 text-amber-400" />
                    <span className="text-sm font-bold text-amber-400">Complete payment to confirm your order</span>
                  </div>
                </div>

                {/* Pay Now Button - Only show when payment is pending */}
                {isPaymentPending && (
                  <Button
                    onClick={handlePayment}
                    className="w-full h-16 rounded-2xl text-base font-black shadow-2xl shadow-primary/20 transition-all hover:-translate-y-1 bg-primary hover:bg-primary/90 text-white gap-2 border-0"
                  >
                    <CreditCard className="size-5" /> 
                    {paymentProvider === 'razorpay' ? 'Pay with Razorpay' : 
                     paymentProvider === 'cashfree' ? 'Pay with Cashfree' : 'Pay Now'}
                  </Button>
                )}

                <Separator className="bg-white/10" />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
